import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Eye,
  Brain,
  Clock,
  Shield,
  Star,
  CheckCircle,
  ArrowRight,
  Cpu,
  Microscope,
  FileText,
  Home,
  DollarSign,
  AlertTriangle,
  ChevronDown,
  Zap
} from 'lucide-react';
import { Helmet } from 'react-helmet';
import './Analysis.css';

const Analysis = () => {
  const [urgencyCount, setUrgencyCount] = useState(87);
  const [openFaq, setOpenFaq] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);

  // Urgency counter
  useEffect(() => {
    const timer = setInterval(() => {
      setUrgencyCount(prev => Math.max(50, prev - 1));
    }, 45000);
    return () => clearInterval(timer);
  }, []);

  // Process steps animation
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentStep(prev => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const benefits = [
    {
      icon: Microscope,
      title: "Análise Preventiva",
      description: "Identifique padrões e sinais que podem lhe auxiliar na prevenção, na manutenção da sua saúde e bem-estar, antes mesmo de se manifestar em sintomas."
    },
    {
      icon: Zap,
      title: "Rapidez Tecnológica",
      description: "Nossa IA processa milhares de pontos de dados em segundos, entregando um relatório completo instantaneamente."
    },
    {
      icon: DollarSign,
      title: "Custo-Benefício Inteligente",
      description: "Por um valor acessível, você tem acesso a uma análise detalhada, com informações valiosas para auxiliar na sua jornada em busca de uma saúde preventiva."
    },
    {
      icon: CheckCircle,
      title: "Simplicidade Total",
      description: "Sem agendamentos ou filas. Basta fotografar seu olho usando qualquer smartphone."
    },
    {
      icon: Shield,
      title: "Privacidade Garantida",
      description: "Seus dados e imagens são processados de forma segura e confidencial pela nossa plataforma."
    },
    {
      icon: Clock,
      title: "Acesso Imediato",
      description: "Não espere dias por um resultado. O futuro da prevenção está disponível para você agora mesmo."
    }
  ];

  const features = [
    "Relatório completo, detalhado e didático",
    "Explicação clara de cada sinal encontrado",
    "Análise do seu perfil mental, emocional e comportamental",
    "Análise de predisposições por sistema do corpo",
    "Sugestões de possíveis exames clínicos",
    "Sugestões de rotinas de autocuidado",
    "Orientações de pontos que merecem sua atenção",
    "Recomendações personalizadas",
  ];

  const testimonials = [
    {
      text: "Incrível! A análise identificou uma predisposição digestiva que eu já sentia, mas não sabia explicar. Agora estou cuidando melhor da minha alimentação e me sinto muito melhor.",
      author: "Ingrid S.",
      avatar: "I"
    },
    {
      text: "Fiquei impressionado com a quantidade de detalhes. Não esperava algo tão completo.",
      author: "Juliana R.",
      avatar: "J"
    },
    {
      text: "Achei que seria algo superficial, mas trouxe informações muito interessantes.",
      author: "Rafael S.",
      avatar: "R"
    },
    {
      text: "Tive dificuldade em tirar as fotos, mas o suporte me ajudou e consegui fazer a análise. O resultado me surpreendeu positivamente.",
      author: "Mariana L.",
      avatar: "M"
    }
  ];

  const faqs = [
    {
      question: "Como funciona a análise?",
      answer: "Você envia fotos dos seus olhos através da nossa plataforma. Nossa IA analisa, identifica os sinais na sua íris e gera um relatório detalhado. Em poucos instantes, você recebe o resultado via email."
    },
    {
      question: "Preciso de algum equipamento?",
      answer: "Apenas de seu smartphone e uma lanterna para auxiliar na iluminação (pode ser de outro celular)."
    },
    {
      question: "Quando recebo o resultado?",
      answer: "Em minutos estará na sua caixa de entrada, logo após o envio das imagens e processamento pela nossa IA."
    },
    {
      question: "Como tiro as fotos dos olhos?",
      answer: "É muito simples! Você pode ver as instruções através do link abaixo. Basicamente, precisa de boa iluminação natural e um celular com câmera. Nosso sistema te guia passo a passo.",
      link: "/how-to-photograph-your-iris",
      linkText: "Veja como fotografar sua íris"
    },
    {
      question: "Por que o preço é tão baixo?",
      answer: "Nossa tecnologia de IA automatiza grande parte do processo, reduzindo custos operacionais. Isso nos permite oferecer uma análise de qualidade por uma fração do preço de consultas tradicionais."
    }
  ];

  const processSteps = [
    {
      title: "Upload da Imagem",
      description: "Você envia as fotos das suas íris, através do seu smartphone",
      icon: Eye,
      color: "from-blue-500 to-cyan-500"
    },
    {
      title: "Análise por IA",
      description: "Nossa IA analisa, detecta padrões e reporta cada detalhe",
      icon: Brain,
      color: "from-purple-500 to-pink-500"
    },
    /*{
      title: "Revisão Especializada",
      description: "Especialista humano valida os resultados",
      icon: CheckCircle,
      color: "from-green-500 to-emerald-500"
    },*/
    {
      title: "Receba sua Análise",
      description: "Relatório detalhado entregue em segundos com recomendações personalizadas",
      icon: FileText,
      color: "from-orange-500 to-red-500"
    }
  ];

  const [expandedTestimonial, setExpandedTestimonial] = useState(null);

  const toggleTestimonial = (index) => {
    setExpandedTestimonial(expandedTestimonial === index ? null : index);
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const [showStickyCta, setShowStickyCta] = useState(false);

  /*useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 500) {
        setShowStickyCta(true);
      } else {
        setShowStickyCta(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);*/

  return (
    <div className="analysis-page">
      <Helmet>
        <title>Análise Preventiva com IA | E-IRIS</title>
        <meta name="description" content="Entenda os sinais do seu corpo, descubra predisposições através da sua íris com a Inteligência Artificial do e-iris. Receba um relatório completo em segundos." />
        <meta name="keywords" content="análise da íris, iridologia IA, saúde preventiva, e-iris, inteligência artificial saúde, diagnóstico íris, prevenir doenças olhos, iridologia digital" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://analysis.eiris.app/" />
        <meta property="og:title" content="Análise Preventiva com IA | E-IRIS" />
        <meta property="og:description" content="Entenda os sinais do seu corpo, descubra predisposições através da sua íris com a Inteligência Artificial do e-iris. Receba um relatório completo e especializado em segundos." />
        {/*<meta property="og:image" content="https://storage.googleapis.com/e-iris-businesses/mac-mobile-home.png" />*/}

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://analysis.eiris.app/" />
        <meta property="twitter:title" content="Análise Preventiva com IA | E-IRIS" />
        <meta property="twitter:description" content="Entenda os sinais do seu corpo, descubra predisposições através da sua íris com a Inteligência Artificial do e-iris. Receba um relatório completo e especializado em segundos." />
        {/*<meta property="twitter:image" content="https://storage.googleapis.com/e-iris-businesses/mac-mobile-home.png" />*/}

        <link rel="canonical" href="https://analysis.eiris.app/" />
      </Helmet>

      {/* Urgency Banner */}
      <div className="urgency-banner">
        🔥 OFERTA DE LANÇAMENTO: Restam apenas {urgencyCount} análises por apenas R$ 19,90!
      </div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="hero-text"
            >
              <h1 className="hero-title">
                <span className="text-gradient">DESCUBRA </span> o que a sua <span className="text-gradient">ÍRIS</span> revela sobre sua <span className="text-gradient">SAÚDE</span> em poucos <span className="text-gradient">segundos</span>
                <br />
                {/*<span className="text-gradient">DESCUBRA</span> o que os seus <span className="text-gradient">OLHOS</span> revelam!
                <br />
               <span className="text-gradient">Análise Preventiva Inteligente!</span> */}
              </h1>

              <p className="hero-subtitle">
                <strong>Análise avançada</strong> por <strong>Inteligência Artificial</strong> que
                identifica padrões na sua íris e detecta predisposições no seu organismo.
              </p>

              {/*<div className="hero-price-tag">
                Apenas <span className="price">R$ 19,90</span>
              </div>

              <div className="hero-bullets">
                <div className="bullet-item">
                  <CheckCircle className="icon" /> <span>Resultado imediato</span>
                </div>
                <div className="bullet-item">
                  <CheckCircle className="icon" /> <span>100% online</span>
                </div>
              </div>*/}


              <div className="cta-wrapper">
                <a
                  href="/request-analysis"
                  className="cta-button final"
                >
                  Fazer Minha Análise Agora <ArrowRight className="icon" />
                </a>
                <p className="microcopy">
                  Pagamento seguro • Entrega imediata • Sem mensalidade
                </p>
              </div>

              {/* Hero Stats (Simplified/Updated) */}
              <div className="hero-stats">
                <div className="stat-item">
                  <div className="stat-icon">
                    <Zap />
                  </div>
                  <div className="stat-number">95%</div>
                  <div className="stat-label">Precisão da IA</div>
                </div>
                <div className="stat-item">
                  <div className="stat-icon">
                    <Clock />
                  </div>
                  <div className="stat-number">60s</div>
                  <div className="stat-label">Tempo de Análise</div>
                </div>
                <div className="stat-item">
                  <div className="stat-icon">
                    <Eye />
                  </div>
                  <div className="stat-number">+200</div>
                  <div className="stat-label">Análises Realizadas</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="section-header"
          >
            <h2>
              Como
              <span className="text-gradient"> Funciona</span>
            </h2>
            <p>Do upload da imagem ao relatório completo em apenas 3 etapas simples</p>
          </motion.div>

          <div className="process-container">
            {/*<div className="process-visual">
              <div className="visual-card">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                    className="step-visualization"
                  >
                    {processSteps[currentStep]?.icon && (
                      <div className={`step-icon bg-gradient-to-r ${processSteps[currentStep]?.color}`}>
                        {React.createElement(processSteps[currentStep]?.icon)}
                      </div>
                    )}
                    <h3>{processSteps[currentStep]?.title}</h3>
                    <p>{processSteps[currentStep]?.description}</p>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>*/}

            <div className="process-steps">
              {processSteps.map((step, index) => {
                const Icon = step.icon;
                const isActive = index === currentStep;

                return (
                  <div
                    key={index}
                    className={`step-item ${isActive ? 'active' : ''}`}
                    onClick={() => setCurrentStep(index)}
                  >
                    <div className={`step-icon-small bg-gradient-to-r ${step.color}`}>
                      <Icon />
                    </div>
                    <div className="step-content">
                      <h4>{step.title}</h4>
                      <p>{step.description}</p>
                    </div>
                    {isActive && (
                      <div className="progress-bar" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Iridology Section */}
      <section className="iridology-section">
        <div className="container">
          <div className="iridology-content">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="iridology-text"
            >
              <h2>
                Iridologia +
                <span className="text-gradient"> IA Inteligente</span> =
                Prevenção Avançada
              </h2>

              <div className="iridology-description">
                <p>
                  A iridologia é uma ciência milenar que estuda os sinais presentes na íris dos olhos
                  para identificar predisposições e desequilíbrios no organismo. Cada região
                  da íris corresponde a diferentes órgãos e sistemas do corpo.
                </p>

                <p>
                  <strong>Nossa inovação:</strong> Combinamos
                  os fundamentos técnicos da iridologia moderna com Inteligência Artificial de última geração,
                  criando uma ferramenta de análise preventiva única no mercado.
                </p>

                <p>
                  Não oferecemos uma consulta iridológica tradicional, mas sim uma
                  <strong> análise preventiva</strong> que
                  identifica padrões e sinais que merecem sua atenção.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="iridology-visual"
            >
              <div className="visual-container">
                <div className="eye-visualization">
                  <Eye className="eye-icon" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Price Section */}
      <section className="price-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="section-header"
          >
            <h2>
              O que você vai receber na
              <span className="text-gradient"> sua análise</span>
            </h2>
            <p>Um mapeamento completo da sua saúde através extraído dos sinais da sua íris</p>
          </motion.div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="feature-item"
              >
                <div className="feature-icon">
                  <CheckCircle />
                </div>
                <span>{feature}</span>
              </motion.div>
            ))}
          </div>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div className="section-cta">
              <a href="/request-analysis" className="cta-button primary">
                QUERO MINHA ANÁLISE AGORA
              </a>
            </div>
          </div>

        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="section-header"
          >
            <h2>
              Por Que Escolher a
              <span className="text-gradient"> Análise Preventiva do E-IRIS</span>?
            </h2>
            <p>Tecnologia de ponta que transforma a forma como você cuida da sua saúde</p>
          </motion.div>

          <div className="benefits-grid">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  viewport={{ once: true }}
                  className="benefit-card"
                >
                  <div className="benefit-icon">
                    <Icon />
                  </div>
                  <h3>{benefit.title}</h3>
                  <p>{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="disclaimer-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="disclaimer-content"
          >
            <div className="disclaimer-header">
              <AlertTriangle />
              <h3>Importante: O que nossa análise NÃO é</h3>
            </div>

            <div className="disclaimer-grid">
              <div className="disclaimer-column">
                <ul>
                  <li>Não é um diagnóstico médico definitivo</li>
                  <li>Não substitui consultas médicas ou exames clínicos</li>
                  <li>Não tem caráter místico ou esotérico</li>
                </ul>
              </div>

              <div className="disclaimer-column">
                <ul>
                  <li>Não prevê o futuro ou faz adivinhações</li>
                  <li>Não dispensa acompanhamento médico regular</li>
                </ul>
              </div>
            </div>

            <p className="disclaimer-footer">
              Nossa análise é uma ferramenta de <strong>prevenção e orientação</strong>,
              baseada em padrões científicos da iridologia moderna.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 5. PROVA SOCIAL (FEEDBACKS) */}
      <section className="testimonials-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="section-header"
          >
            <h2>
              O que as pessoas
              <span className="text-gradient"> estão dizendo</span>
            </h2>
            <p>Relatos  de pessoas que já descobriram o futuro da prevenção</p>
          </motion.div>

          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className={`testimonial-card ${expandedTestimonial === index ? 'expanded' : ''}`}
                onClick={() => testimonial.text.length > 120 && toggleTestimonial(index)}
              >
                <div className="testimonial-header">
                  <div className="avatar">
                    {testimonial.avatar}
                  </div>
                  <div className="author-info">
                    <h4>{testimonial.author}</h4>
                    <p>{testimonial.role}</p>
                  </div>
                </div>

                <p className="testimonial-text">
                  "{testimonial.text}"
                </p>

                {testimonial.text.length > 120 && (
                  <button className="read-more-btn">
                    {expandedTestimonial === index ? 'Ver menos' : 'Ver mais'}
                  </button>
                )}

                <div className="stars">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="star-filled" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="faq-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="section-header"
          >
            <h2>
              Dúvidas
              <span className="text-gradient"> Frequentes</span>
            </h2>
            <p>Tudo o que você precisa saber sobre sua análise</p>
          </motion.div>

          <div className="faq-container">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="faq-item"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="faq-question"
                >
                  <span>{faq.question}</span>
                  <ChevronDown className={`chevron ${openFaq === index ? 'rotated' : ''}`} />
                </button>

                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="faq-answer"
                    >
                      <div className="answer-content">
                        {faq.answer}
                      </div>
                      {faq.link && (
                        <div className="answer-content" style={{ padding: "5px 24px 24px 24px" }}>
                          <a href={faq.link} target="_blank" rel="noopener noreferrer">
                            {faq.linkText || "Saiba mais"}
                          </a>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. CTA FINAL (REFORÇO) */}
      <section className="final-cta-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="final-cta-content"
          >
            <h2>
              Não Espere os Sintomas
              <span className="text-accent"> Aparecerem</span>
            </h2>
            <p>
              O futuro da prevenção está aqui. Descubra hoje o que seu corpo está tentando te dizer
              através da tecnologia mais avançada em análise iridológica.
            </p>

            <div className="final-price-card">
              <div className="price-label">APENAS</div>
              <div className="price-value">R$ 19,90</div>
              <div className="offer-text">
                🔥 60% OFF - Análise Completa Preventiva
              </div>
            </div>

            <div className="cta-wrapper">
              <a
                href="/request-analysis"
                className="cta-button final"
              >
                Fazer minha análise agora <ArrowRight className="icon" />
              </a>
              {/*<h5 className="microcopy">
                Rápido • Simples • Seguro
              </h5>*/}
            </div>
          </motion.div>
        </div>
      </section>
      {/* Sticky CTA Bar */}
      <div className={`sticky-cta ${showStickyCta ? 'visible' : ''}`}>
        <div className="sticky-cta-price">
          Análise por apenas R$ 19,90
        </div>
        <a href="/request-analysis" className="cta-button primary">
          Fazer minha análise agora
        </a>
      </div>
    </div>
  );
};

export default Analysis;
